/**
 * Planes y límites (SDD SaaS §14.1, PLAN §3.2). Una sola fuente para la API,
 * la web y la extensión. Los precios son finales (IVA incluido) en CLP.
 */
import type { PlanId } from './types'

export interface PlanLimits {
  id: PlanId
  nombre: string
  /** Minutos incluidos por período (por asiento en Equipo). */
  minutosIncluidos: number
  /** Duración máxima de una grabación o importación, en minutos. */
  topeGrabacionMin: number
  /** Tamaño máximo de una importación, en MB. */
  topeImportMb: number
  /** Importaciones por día (null = sin tope). */
  importsPorDia: number | null
  /** Regeneraciones manuales de notas por organización y día. */
  regeneracionesPorDia: number
  /** Preguntas a la IA por organización y día. */
  preguntasPorDia: number
  /** Correcciones de notas por chat, por organización y día (aparte de las regeneraciones). */
  correccionesPorDia: number
  /** Versiones máximas por plantilla y grabación. */
  versionesPorPlantilla: number
  /** Plantillas de notas propias por organización. */
  plantillasPersonalizadas: number
  /** Webhooks salientes (integraciones) por organización. */
  webhooks: number
  /** Temas a seguir por organización. */
  temas: number
  /** Destinatarios de notas por correo por organización y día (cuida la reputación del dominio). */
  enviosNotasPorDia: number
  /** Minutos procesados por organización y día antes de entrar en `held`. */
  maxMinutosOrgDia: number
  /** Precio mensual final en CLP (IVA incluido); null = gratis o a cotizar. */
  precioClpMes: number | null
  /** Disponible en v1. */
  disponible: boolean
}

export const PLANS: Record<PlanId, PlanLimits> = {
  free: {
    id: 'free',
    nombre: 'Gratis',
    minutosIncluidos: 120,
    topeGrabacionMin: 90,
    topeImportMb: 200,
    importsPorDia: 5,
    regeneracionesPorDia: 3,
    preguntasPorDia: 10,
    correccionesPorDia: 5,
    versionesPorPlantilla: 3,
    plantillasPersonalizadas: 1,
    webhooks: 0,
    temas: 3,
    enviosNotasPorDia: 5,
    maxMinutosOrgDia: 240,
    precioClpMes: null,
    disponible: true
  },
  personal: {
    id: 'personal',
    nombre: 'Personal',
    minutosIncluidos: 600,
    topeGrabacionMin: 240,
    topeImportMb: 1024,
    importsPorDia: null,
    regeneracionesPorDia: 10,
    preguntasPorDia: 60,
    correccionesPorDia: 30,
    versionesPorPlantilla: 3,
    plantillasPersonalizadas: 10,
    webhooks: 3,
    temas: 20,
    enviosNotasPorDia: 50,
    maxMinutosOrgDia: 1200,
    precioClpMes: 6990,
    disponible: true
  },
  equipo: {
    id: 'equipo',
    nombre: 'Equipo',
    minutosIncluidos: 600,
    topeGrabacionMin: 240,
    topeImportMb: 1024,
    importsPorDia: null,
    regeneracionesPorDia: 30,
    preguntasPorDia: 120,
    correccionesPorDia: 100,
    versionesPorPlantilla: 3,
    plantillasPersonalizadas: 30,
    webhooks: 10,
    temas: 50,
    enviosNotasPorDia: 200,
    maxMinutosOrgDia: 3600,
    precioClpMes: 5990,
    disponible: true
  }
}

/** Límite absoluto de duración de cualquier grabación (SDD §3.4). */
export const ABSOLUTE_MAX_RECORDING_MIN = 8 * 60
/** Aviso al usuario cuando la grabación supera esta duración. */
export const LONG_RECORDING_WARNING_MIN = 5 * 60
/** Tope recomendado por grabación (SDD §3.4). */
export const RECOMMENDED_MAX_RECORDING_MIN = 6 * 60

/** Margen de minutos que se tolera por encima del saldo al completar (SDD §14.4). */
export const QUOTA_GRACE_MIN = 60
/** Mínimo de minutos restantes para iniciar una grabación sin retenerla. */
export const QUOTA_MIN_TO_START = 5
/** Días que una grabación retenida espera en `held/` antes de descartarse. */
export const HELD_RETENTION_DAYS = 7

/** IVA aplicado en Chile a servicios digitales (informativo: el precio ya lo incluye). */
export const IVA_CL = 0.19

/** Minutos incluidos para una organización según plan y asientos. */
export function includedMinutes(plan: PlanId, asientos = 1): number {
  const p = PLANS[plan]
  return plan === 'equipo' ? p.minutosIncluidos * Math.max(1, asientos) : p.minutosIncluidos
}

/** Neto que recibe eco antes de la comisión del merchant of record. */
export function netoSinIva(precioFinalClp: number): number {
  return Math.round(precioFinalClp / (1 + IVA_CL))
}

/** Formatea minutos como "7 h 20 min" (nunca minutos sueltos por encima de 60). */
export function formatMinutes(min: number): string {
  const m = Math.max(0, Math.round(min))
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h === 0) return `${r} min`
  if (r === 0) return `${h} h`
  return `${h} h ${r} min`
}
